import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import * as paymentRequestActions from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.actions";
import * as paymentRequestSelectors from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.selectors";
import { useDetail } from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import { EPaymentRequestFollowUpType } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { mapObjectWithTimeZonedDatesToUtcIsoString } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { usePrevious } from "@rentiohq/shared-frontend/dist/utils/hooks.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { getPaymentOrders } from "@rentiohq/shared-frontend/dist/utils/paymentRequest.utils";
import { Loading, MultiStepForm } from "@rentiohq/web-shared/dist/components";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import { createTitle } from "../../../utils/payment";
import createFollowUpRegisteredMailForm from "../forms/followUpRegisteredMail";
import { IPaymentFollowUpScreenProps } from "../PaymentFollowUpModal.types";

const formId = generateFormId();

export const RegisteredMailScreen = React.forwardRef(
  (
    props: Omit<IPaymentFollowUpScreenProps, "setPostBody" | "postBody">,
    formRef: any,
  ) => {
    const { followUpItem, onClose, renderActivities, setIsLoading, contact } =
      props;

    const dispatch = useDispatch();

    const propertyId = followUpItem.requestPropertyId;

    const { detail: property, isFetching } = useDetail({
      id: propertyId,
      shouldRefetch: false,
    });

    const isPendingFollowUp =
      useSelector((state: IRootStore) =>
        paymentRequestSelectors.isPendingFollowUp(
          state,
          followUpItem.id,
          EPaymentRequestFollowUpType.RegisteredMail,
        ),
      ) || false;
    const followUpError =
      useSelector((state: IRootStore) =>
        paymentRequestSelectors.followUpError(
          state,
          followUpItem.id,
          EPaymentRequestFollowUpType.RegisteredMail,
        ),
      ) || false;
    const prevIsPendingFollowUp = usePrevious(isPendingFollowUp);

    React.useEffect(() => {
      setIsLoading?.(isPendingFollowUp);
    }, [isPendingFollowUp, setIsLoading]);

    React.useEffect(() => {
      if (!!prevIsPendingFollowUp && !isPendingFollowUp && !followUpError) {
        onClose?.();
      }
    }, [prevIsPendingFollowUp, isPendingFollowUp, followUpError]);

    const handleSuccess = async (values: any) => {
      const { address: inputAddress, ...data } =
        mapObjectWithTimeZonedDatesToUtcIsoString(values);
      const { country: countryId, ...address } = inputAddress;
      dispatch(
        paymentRequestActions.reminderRegisteredMailStart.getAction({
          id: followUpItem.id,
          data: { ...data, ...address, countryId },
        }),
      );
    };

    if (isFetching) {
      return <Loading />;
    }

    if (!contact) {
      return null;
    }

    if (!property) {
      return null;
    }

    return (
      <>
        <MultiStepForm
          ref={formRef}
          formId={`follow-up-registered-mail-${formId}`}
          preventSubmitThroughEnter={true}
          // schemas={schemas}
          schemas={createFollowUpRegisteredMailForm({
            subject: `${getLocalizedText(
              "payments.follow_up.registered_mail.heading",
              undefined,
              undefined,
              contact.locale,
            )}: ${createTitle({
              paymentOrders: getPaymentOrders(followUpItem),
              paymentRequest: followUpItem,
            })}`,
            content: getLocalizedText(
              "payments.follow_up.registered_mail.content",
              // @ts-ignore-next-line
              { ...contact },
              undefined,
              contact.locale,
            ),
            contact,
            property,
          })}
          showControls={false}
          withAside={false}
          onSuccess={handleSuccess}
          isLoading={isPendingFollowUp}
          getPrecommitConfirm={(params: { onProceed: () => void }) => {
            const { onProceed } = params;

            return {
              type: "warning",
              title: getLocalizedText(
                "payments.follow_up.registered_mail.heading",
              ),
              info: getLocalizedText(
                "payments.follow_up.registered_mail.confirm",
              ),
              primaryActions: [
                {
                  title: getLocalizedText("system.confirm"),
                  onPress: () => {
                    onProceed();
                  },
                },
              ],
              secondaryAction: {
                title: getLocalizedText("system.previous"),
              },
            };
          }}
        />
        {renderActivities}
      </>
    );
  },
);
